import React, { useEffect } from "react"
import PropTypes from "prop-types"
import { Box, Divider, Paper, Grid } from "@mui/material"
import i18n from "i18n"

import {
  getInvoiceSubtotal,
  getInvoiceImpuestos,
  getInvoiceRetenciones,
  getInvoiceTotal,
  getGroupedTaxes,
  getGroupedRetentions,
  round2,
} from "../utils/invoiceUtils"

const fmt = value =>
  value.toLocaleString("es-ES", { style: "currency", currency: "EUR" })

const InvoiceTotals = ({
  concepts,
  setTotals,
  globalDiscount = 0,
  corrections = [],
}) => {
  const subtotal = getInvoiceSubtotal(concepts)
  const globalDiscountAmount = round2(subtotal * (globalDiscount / 100))
  const subtotalAfterGlobalDiscount = round2(subtotal - globalDiscountAmount)

  const impuestos = getInvoiceImpuestos(concepts, globalDiscount)
  const retenciones = getInvoiceRetenciones(concepts, globalDiscount)
  const total = getInvoiceTotal(concepts, globalDiscount)

  const groupedTaxes = getGroupedTaxes(concepts, globalDiscount)
  const groupedRetentions = getGroupedRetentions(concepts, globalDiscount)

  const issuedCredits = corrections.filter(
    c => c.issued && c.type === "credit_note",
  )
  const issuedDebits = corrections.filter(
    c => c.issued && c.type === "debit_note",
  )
  const totalCredits = round2(
    issuedCredits.reduce((sum, c) => sum + (Number(c.total) || 0), 0),
  )
  const totalDebits = round2(
    issuedDebits.reduce((sum, c) => sum + (Number(c.total) || 0), 0),
  )
  const netBalance = round2(total - totalCredits + totalDebits)
  const hasNetBalance = issuedCredits.length > 0 || issuedDebits.length > 0

  useEffect(() => {
    if (typeof setTotals === "function") {
      setTotals({
        subtotal: subtotalAfterGlobalDiscount,
        tax: impuestos,
        retention: retenciones,
        total,
      })
    }
  }, [subtotal, globalDiscountAmount, impuestos, retenciones, total])

  return (
    <Grid item xs={12} sm={6} md={5} lg={4}>
      <Paper elevation={0} sx={{ p: 2, border: "1px solid #eee" }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <span>{i18n.t("invoices.subtotal")}</span>
          <span>{fmt(subtotal)}</span>
        </Box>
        {globalDiscount > 0 && (
          <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
            <span>
              {i18n.t("invoices.discount")} ({globalDiscount}%)
            </span>
            <span>-{fmt(globalDiscountAmount)}</span>
          </Box>
        )}
        {groupedTaxes.map((tax, idx) => (
          <Box
            key={`tax-${idx}`}
            sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
          >
            <span>{tax.name}</span>
            <span>{fmt(tax.amount)}</span>
          </Box>
        ))}
        {groupedRetentions.map((ret, idx) => (
          <Box
            key={`ret-${idx}`}
            sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
          >
            <span>{ret.name}</span>
            <span>-{fmt(ret.amount)}</span>
          </Box>
        ))}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: 600,
            mt: 2,
          }}
        >
          <span>{i18n.t("invoices.total")}</span>
          <span>{fmt(total)}</span>
        </Box>

        {hasNetBalance && (
          <>
            <Divider sx={{ my: 2 }} />
            {issuedCredits.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                  color: "text.secondary",
                }}
              >
                <span>{i18n.t("invoices.issuedCreditNotes")}</span>
                <span>-{fmt(totalCredits)}</span>
              </Box>
            )}
            {issuedDebits.length > 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 1,
                  color: "text.secondary",
                }}
              >
                <span>{i18n.t("invoices.issuedDebitNotes")}</span>
                <span>+{fmt(totalDebits)}</span>
              </Box>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                fontWeight: 700,
                mt: 1,
              }}
            >
              <span>{i18n.t("invoices.netBalance")}</span>
              <span>{fmt(netBalance)}</span>
            </Box>
          </>
        )}
      </Paper>
    </Grid>
  )
}

InvoiceTotals.propTypes = {
  concepts: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      quantity: PropTypes.number,
      price: PropTypes.number,
      taxes: PropTypes.array,
      retentions: PropTypes.array,
    }),
  ).isRequired,
  setTotals: PropTypes.func,
  globalDiscount: PropTypes.number,
  corrections: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      issued: PropTypes.bool,
      total: PropTypes.number,
    }),
  ),
}

export default InvoiceTotals
